import type {
  ExtractionApiResponse,
  ExtractionTypeOption,
} from './AIVoiceExtraction.types';

export const EXTRACTION_API_URL = 'https://vidai-ai.in/speech-to-emr/convert';

export const EXTRACTION_TYPE_OPTIONS: ExtractionTypeOption[] = [
  {
    key: 'medical_history',
    label: 'Medical History',
    description: 'Past and current illnesses, diagnoses, and clinical conditions.',
  },
  // {
  //   key: 'medical_history_m',
  //   label: 'Medical History Male',
  //   description: 'Past and current illnesses, diagnoses, and clinical conditions.',
  // },
  {
    key: 'oocyte_grading',
    label: 'Oocyte Grading',
    description: 'Assessment of oocyte quality and developmental potential.',
  },
  {
    key: 'embryo_culture',
    label: 'Embryo Culture',
    description: 'Assessment of embryo quality and developmental potential.',
  },
];

export const formatLabel = (value: string): string =>
  value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

export const hasMeaningfulValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value as Record<string, unknown>).length > 0;
  return true;
};

export const getStructuredExtractionData = (
  response: ExtractionApiResponse
): Record<string, unknown> => {
  if (response.emr_data && typeof response.emr_data === 'object') {
    return response.emr_data;
  }

  if (response.extracted_data && typeof response.extracted_data === 'object') {
    return response.extracted_data;
  }

  if (response.data && typeof response.data === 'object') {
    return response.data;
  }

  const fallback = { ...response };
  delete fallback.transcription_id;
  delete fallback.emr_id;
  delete fallback.ai_summary;
  delete fallback.summary;

  return fallback;
};

export const buildAiSummaryLines = (
  response: ExtractionApiResponse,
  selectedType: ExtractionTypeOption
): string[] => {
  const providedSummary =
    typeof response.ai_summary === 'string'
      ? response.ai_summary
      : typeof response.summary === 'string'
        ? response.summary
        : '';

  if (providedSummary.trim()) {
    return providedSummary
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean);
  }

  const structured = getStructuredExtractionData(response);
  const topLevelEntries = Object.entries(structured).filter(([, value]) =>
    hasMeaningfulValue(value)
  );

  if (!topLevelEntries.length) {
    return [
      `AI extraction completed for ${selectedType.label}.`,
      'No strongly structured fields were returned by the API.',
    ];
  }

  const sectionNames = topLevelEntries
    .map(([key]) => formatLabel(key))
    .slice(0, 6);

  const summaryLines = [
    `AI extraction completed for ${selectedType.label}.`,
    `Detected ${topLevelEntries.length} populated section${
      topLevelEntries.length > 1 ? 's' : ''
    }.`,
    `Primary extracted sections: ${sectionNames.join(', ')}.`,
  ];

  if (response.transcription_id) {
    summaryLines.push(`Transcription ID: ${String(response.transcription_id)}.`);
  }

  if (response.emr_id) {
    summaryLines.push(`EMR ID: ${String(response.emr_id)}.`);
  }

  return summaryLines;
};

export const convertTranscriptToExtraction = async (
  transcriptText: string,
  selectedType: ExtractionTypeOption
): Promise<ExtractionApiResponse> => {
  const response = await fetch(EXTRACTION_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      transcript_text: transcriptText,
      field: selectedType.key,
    }),
  });

  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const responseBody = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const detail =
      isJson &&
      responseBody &&
      typeof responseBody === 'object' &&
      'detail' in responseBody &&
      responseBody.detail
        ? String(responseBody.detail)
        : `HTTP ${response.status}`;

    throw new Error(detail);
  }

  return (responseBody ?? {}) as ExtractionApiResponse;
};